import {
  Container,
  Dialog,
  DialogContent,
  Grid,
  Typography,
  TextField,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import NumberTextField from "../../common/NumberTextField";
import { useState, useEffect, useCallback } from "react";
import { NewDwellTimeUptickInput } from "../../../graphql/generated";
import { useTranslation } from "react-i18next";
import { DesktopDateTimePicker } from "@mui/x-date-pickers";
import { formatInTimeZone, zonedTimeToUtc, utcToZonedTime } from "date-fns-tz";
import { format, startOfDay, endOfDay } from "date-fns";
import { testIds } from "./DwellTimeUptickForm.testIds";

const fallbackTimezone = "America/Los_Angeles";

export type DwellTimeUptickDialogFormProps = {
  open: boolean;
  onClose: () => void;
  onSave: (dwellTimeUptick: NewDwellTimeUptickInput) => Promise<void>;
  businessEntityId: string;
  businessEntityTimezone?: string | null;
  editingUptick?: any | null;
};

export default function DwellTimeUptickDialogForm({
  open,
  onClose,
  onSave,
  businessEntityId,
  businessEntityTimezone,
  editingUptick,
}: DwellTimeUptickDialogFormProps) {
  const { t } = useTranslation(["common", "business"]);
  const timezone = businessEntityTimezone || fallbackTimezone;
  const [saving, setSaving] = useState(false);

  const getInitialState = useCallback(
    () => ({
      startDate: startOfDay(new Date()),
      endDate: endOfDay(new Date()),
      addedTimeInMinutes: 0,
      businessEntityId: businessEntityId,
    }),
    [businessEntityId],
  );

  const [localDwellTimeUptick, setLocalDwellTimeUptick] =
    useState<NewDwellTimeUptickInput>(getInitialState());

  // Reset form when dialog opens or when editing a different uptick
  useEffect(() => {
    if (open) {
      if (editingUptick) {
        // Convert UTC dates to the business entity's timezone for display
        const startDate = editingUptick.startDate
          ? utcToZonedTime(new Date(editingUptick.startDate), timezone)
          : new Date();
        const endDate = editingUptick.endDate
          ? utcToZonedTime(new Date(editingUptick.endDate), timezone)
          : new Date();

        setLocalDwellTimeUptick({
          startDate,
          endDate,
          addedTimeInMinutes: editingUptick.addedTimeInMinutes || 0,
          businessEntityId: businessEntityId,
        });
      } else {
        setLocalDwellTimeUptick(getInitialState());
      }
    }
  }, [open, editingUptick, businessEntityId, timezone, getInitialState]);

  // Helper function to convert a date from the picker (local browser time)
  // to the business entity's timezone, then to UTC for storage
  const convertToTimezoneAwareDate = (date: Date | null): Date | null => {
    if (!date) return null;

    // Format the date as if it were in the business entity's timezone
    const dateString = format(date, "yyyy-MM-dd'T'HH:mm:ss");

    // Parse it as being in the business entity's timezone and convert to UTC
    return zonedTimeToUtc(dateString, timezone);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Convert the dates from the business entity's timezone to UTC before saving
      const uptickToSave = {
        ...localDwellTimeUptick,
        startDate: convertToTimezoneAwareDate(localDwellTimeUptick.startDate),
        endDate: convertToTimezoneAwareDate(localDwellTimeUptick.endDate),
      };
      await onSave(uptickToSave);

      // Clear the form after successful save
      setLocalDwellTimeUptick(getInitialState());
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogContent sx={{ padding: 2 }}>
        <Container maxWidth="sm">
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6">
                {editingUptick
                  ? t("business:editDwellTimeUptick", "Edit Dwell Time Uptick")
                  : t("business:dwellTimeUptick", "Dwell Time Uptick")}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t("common:timezone", "Timezone")}:{" "}
                {formatInTimeZone(new Date(), timezone, "zzz")} ({timezone})
              </Typography>
            </Grid>
            <Grid item xs={12} spacing={2}>
              <DesktopDateTimePicker
                label={t("business:startDate", "Start Date & Time")}
                value={localDwellTimeUptick.startDate}
                onChange={(date) => {
                  setLocalDwellTimeUptick({
                    ...localDwellTimeUptick,
                    startDate: date as Date | null,
                  });
                }}
                ampm={false}
                renderInput={(params) => (
                  <TextField {...params} fullWidth sx={{ marginBottom: 2 }} />
                )}
              />
              <DesktopDateTimePicker
                label={t("business:endDate", "End Date & Time")}
                minDateTime={localDwellTimeUptick.startDate}
                value={localDwellTimeUptick.endDate}
                onChange={(date) => {
                  setLocalDwellTimeUptick({
                    ...localDwellTimeUptick,
                    endDate: date as Date | null,
                  });
                }}
                ampm={false}
                renderInput={(params) => (
                  <TextField {...params} fullWidth sx={{ marginBottom: 2 }} />
                )}
              />
              <NumberTextField
                label={t(
                  "business:addedTimeInMinutes",
                  "Added Time in Minutes",
                )}
                fullWidth
                value={localDwellTimeUptick.addedTimeInMinutes}
                onChange={(event) => {
                  setLocalDwellTimeUptick({
                    ...localDwellTimeUptick,
                    addedTimeInMinutes: Number(event.target.value),
                  });
                }}
                sx={{ marginBottom: 2 }}
              />
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <LoadingButton data-testid={testIds.dwellTimeUptickFormSave}
              variant="contained"
              color="primary"
              onClick={handleSave}
              loading={saving}
            >
              {t("common:save", "Save")}
            </LoadingButton>
          </Grid>
        </Container>
      </DialogContent>
    </Dialog>
  );
}
